import React, { useEffect, useState } from "react";
import { UserPlus, Copy, Check, TreeStructure } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, endpoints } from "@/lib/api";
import { copyToClipboard } from "@/lib/download";

export const ReferralCard = ({ user }) => {
    const [referral, setReferral] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get(endpoints.referrals(user.user_id))
            .then((r) => setReferral(r.data))
            .catch(() => setReferral(null));
    }, [user]);

    if (!user || !referral) return null;

    const shareUrl = `${window.location.origin}/?ref=${referral.referral_code}`;

    const handleCopy = async () => {
        const ok = await copyToClipboard(shareUrl);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success("Referral link copied", {
                description: "When they join, you both unlock the Refer-a-Friend milestone.",
            });
        }
    };

    return (
        <div
            data-testid="referral-card"
            className="grid grid-cols-1 gap-6 rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6 sm:p-8 md:grid-cols-[1fr_auto] md:items-center md:gap-8"
        >
            <div>
                <div className="flex items-center gap-2 text-zinc-500">
                    <UserPlus size={14} weight="fill" color="#a3e635" />
                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                        Refer a friend · Both plant a tree
                    </span>
                </div>
                <h3 className="mt-3 font-editorial text-xl leading-tight text-white sm:text-2xl">
                    Turn one Tesla owner into two.
                </h3>
                <p className="mt-2 text-sm text-zinc-500">
                    When a friend joins EVIQ with your link, you both unlock the milestone and the
                    community counter ticks up in real time.
                </p>

                <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-md border border-[#1f1f22] bg-[#050505] px-3 py-2">
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            code
                        </span>
                        <span
                            data-testid="referral-code"
                            className="truncate font-mono-metric text-sm text-white"
                        >
                            {shareUrl}
                        </span>
                    </div>
                    <button
                        type="button"
                        data-testid="referral-copy-btn"
                        onClick={handleCopy}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#a3e635] px-4 py-2 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                    >
                        {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                        {copied ? "Copied" : "Copy link"}
                    </button>
                </div>
            </div>

            <div className="flex flex-row items-stretch gap-3 md:flex-col">
                <div className="flex-1 rounded-md border border-[#1f1f22] bg-[#050505] p-4 text-center md:min-w-[140px]">
                    <div className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                        Friends joined
                    </div>
                    <div
                        data-testid="referral-count"
                        className="mt-2 font-mono-metric text-3xl font-bold text-white"
                    >
                        {referral.count}
                    </div>
                </div>
                <div className="flex-1 rounded-md border border-[#a3e635]/30 bg-[#0a0f05] p-4 text-center md:min-w-[140px]">
                    <TreeStructure size={16} color="#a3e635" weight="fill" className="mx-auto" />
                    <div className="mt-2 font-mono-metric text-[10px] uppercase tracking-[0.24em] text-[#a3e635]">
                        Trees unlocked
                    </div>
                    <div className="mt-1 font-mono-metric text-3xl font-bold text-white">
                        {referral.trees_unlocked}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReferralCard;
